import { Container } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { ObjectiveLoadErrorMessage } from './components/ObjectivePageStates';
import PillarObjectivesPeople from './components/PillarObjectivesPeople';
import PillarPageHero from './components/PillarPageHero';
import { usePillarPageView } from './hooks/usePillarPageView';

import { useGetCompanyInformation } from '@/api/directory';
import { useGetCompanyPillarSummary, useGetObjectives } from '@/api/objectives';
import { BackLink } from '@/components/BackLink';
import ObjectiveListToolbar from '@/components/objectives/ObjectiveListToolbar';
import {
    OBJECTIVES_PAGE_LAYOUT,
    OWNER_LIST_PANEL,
    SECTION_TITLE_TIGHT,
} from '@/components/objectives/objectiveStyles';
import PillarScopeBar from '@/components/objectives/PillarScopeBar';
import { useObjectiveAbilities } from '@/hooks/objectives/useObjectiveAbilities';
import { useObjectivesStepBack } from '@/hooks/objectives/useObjectivesStepBack';
import { usePeriodOptions, usePeriodParam } from '@/hooks/objectives/usePeriodOptions';
import { isResponseError } from '@/hooks/utils/useError';
import { objectivesLensPath } from '@/navigation/objectivesRoutes';
import { isGuid } from '@/utils/guid';

// Every branch below (error, not-found, loaded) renders the same page shell, so its className is
// shared instead of repeated.
const PAGE_LAYOUT_CLASSNAME = OBJECTIVES_PAGE_LAYOUT.shell;

/**
 * A pillar's page, read through the lens the objectives page left in the address: the pillar
 * scope, the list's filters and the period. The header counts the pillar in that scope, the way its
 * tile does: company-wide under "organization", among the list's objectives otherwise; the list
 * below stays within what the reader may open, narrowed by the filters alone.
 */
export default function PillarObjectivesPage() {
    const { t } = useTranslation();
    const { search } = useLocation();
    const { stepBackOr } = useObjectivesStepBack();
    const { pillarId } = useParams<{ pillarId: string }>();
    // An id that is not a GUID -- a link cut short or mistyped -- names no pillar: it reads as not
    // found without asking the server, whose list would refuse it outright.
    const isWellFormedPillarId = isGuid(pillarId);

    const { canManageOthersObjectives } = useObjectiveAbilities();
    const { data: company } = useGetCompanyInformation();
    const {
        options: periodOptions,
        defaultPeriodId,
        isLoading: isLoadingPeriods,
        isError: arePeriodsInError,
    } = usePeriodOptions();
    const { window: periodWindow } = usePeriodParam(periodOptions, defaultPeriodId, {
        allowCustomDates: true,
    });

    const {
        data: companyPillar,
        isLoading: isLoadingPillar,
        error: pillarError,
    } = useGetCompanyPillarSummary(pillarId, periodWindow, isWellFormedPillarId);
    const {
        data: objectives = [],
        isLoading: isLoadingObjectives,
        error: objectivesError,
    } = useGetObjectives({ ...periodWindow, pillarId }, isWellFormedPillarId);

    const {
        filters,
        roster,
        owners,
        statusFilter,
        pillarScope,
        keepsDraftsOnly,
        scopedFigures,
        filteredTeam,
        heroPillar,
        partialListCounts,
        emptyMessageKey,
    } = usePillarPageView({
        objectives,
        objectivesRead: { isLoading: isLoadingObjectives, hasError: !!objectivesError },
        companyPillar,
    });

    const isNotFound =
        !isWellFormedPillarId ||
        (isResponseError(pillarError) && pillarError.response?.status === 404);
    // Any failed read shows the loading error rather than a partial answer: without the cycles the
    // header would count every cycle, without the employee batch every group would read "no owner".
    const hasPageError =
        arePeriodsInError || (!!pillarError && !isNotFound) || !!objectivesError || !!owners.error;
    // Until the period options arrive the queries above run unfiltered: nothing read before then
    // is the selected period's.
    const isListLoading =
        isLoadingPeriods || isLoadingObjectives || owners.isLoading || roster.isResolvingFilter;
    const isHeroLoading = isLoadingPeriods || isLoadingPillar || scopedFigures.isLoading;

    // Under "organization" the header counts the whole company and the list what the reader may
    // open: when the list holds fewer, a sentence says why rather than letting it look incomplete.
    const partialListNote =
        partialListCounts && !isListLoading
            ? t('objectives.pillarPage.partialList', partialListCounts)
            : undefined;

    const goBack = () => stepBackOr({ path: objectivesLensPath(search) });

    const backLink = (
        <BackLink
            onClick={goBack}
            dataTestId={'pillar-objectives-back'}
            label={t('objectives.pillarPage.back')}
            className={'mb-5'}
        />
    );

    if (hasPageError) {
        return (
            <Container
                className={PAGE_LAYOUT_CLASSNAME}
                dataTestId={'pillar-objectives-page'}
            >
                <ObjectiveLoadErrorMessage />
            </Container>
        );
    }

    if (isNotFound) {
        return (
            <Container
                className={PAGE_LAYOUT_CLASSNAME}
                dataTestId={'pillar-objectives-page'}
            >
                <div className={'flex flex-col items-start'}>
                    {backLink}

                    <p className={'text-obj-slate'}>{t('objectives.pillarPage.notFound')}</p>
                </div>
            </Container>
        );
    }

    return (
        <Container
            className={PAGE_LAYOUT_CLASSNAME}
            dataTestId={'pillar-objectives-page'}
        >
            {backLink}

            <PillarScopeBar
                pillarScope={pillarScope}
                subject={'pillar'}
                keepsDraftsOnly={keepsDraftsOnly}
                filteredTeam={filteredTeam}
            />

            <PillarPageHero
                isLoading={isHeroLoading}
                hasError={scopedFigures.hasError}
                pillar={heroPillar}
            />

            <div className={'mt-6'}>
                <ObjectiveListToolbar
                    filters={filters}
                    company={company}
                    canNarrowRoster={canManageOthersObjectives}
                    statusMenu={statusFilter.menuGroup}
                />
            </div>

            <section
                className={OWNER_LIST_PANEL}
                data-testid={'pillar-objectives-people'}
            >
                <h2 className={`${SECTION_TITLE_TIGHT} mb-1`}>
                    {t('objectives.pillarPage.peopleTitle')}
                </h2>

                <div className={'mb-9 flex flex-col gap-2'}>
                    <p className={'text-obj-slate text-sm font-medium'}>
                        {t('objectives.pillarPage.peopleSubtitle')}
                    </p>

                    {partialListNote && (
                        <p className={'text-obj-slate text-sm'}>{partialListNote}</p>
                    )}
                </div>

                <PillarObjectivesPeople
                    groups={statusFilter.groups}
                    employeesById={owners.employeesById}
                    isLoading={isListLoading}
                    hasFilterError={roster.hasFilterError}
                    emptyMessageKey={emptyMessageKey}
                />
            </section>
        </Container>
    );
}
